// 소스·에셋 보관함: 로고·키비주얼·참고 그림을 올리고, LED 화면에 띄울 그림(에셋)을 따로 만든다.
// 무대 디자인(1차)과 연출컷(2차)에서 같은 보관함을 쓰고, 단계마다 붙일 것을 고른다.
import type { Asset, AssetKind, Cut, Project } from "../../shared/types";
import { icon } from "../icons";
import { api, busy, clear, confirmBox, h, modal, mediaUrl, nudgeQueued, pickFiles, redraw, segmented, shrinkImage, toast, withCost } from "../lib";
import { refreshMe, state } from "../store";
import type { Ctx } from "./project";

const KINDS: { id: AssetKind; ko: string; hint: string }[] = [
  { id: "logo", ko: "로고", hint: "글자·모양 그대로 넣을 로고" },
  { id: "kv", ko: "키비주얼", hint: "색과 그래픽 모티프를 가져올 그림" },
  { id: "led", ko: "LED 화면", hint: "어느 화면에 그대로 띄울 그림" },
  { id: "ref", ko: "참고 그림", hint: "소품·의상·분위기 등 이것만 참고" },
];
const ASPECTS = [
  { id: "16:9", ko: "16:9 가로", hint: "메인 LED", w: 16, h: 9 },
  { id: "21:9", ko: "21:9 와이드", hint: "긴 띠·원통 펼친 면", w: 21, h: 9 },
  { id: "9:16", ko: "9:16 세로", hint: "세로 LED·타워", w: 9, h: 16 },
  { id: "1:1", ko: "1:1 정사각", hint: "정사각 화면", w: 1, h: 1 },
];

export const MAX_USED = 4;

/** LED 로 볼 수 있는 요소 (화면을 지정할 때 고르는 목록) */
const screens = (ctx: Ctx) => ctx.p.data.elements.filter((e) => e.status === "confirmed" && (/led|imag|tower/.test(e.kind) || /LED|화면|스크린/.test(e.name)));

/** 비율 모양 작은 그림 */
function ratioIcon(w: number, hh: number): HTMLElement {
  const s = 14 / Math.max(w, hh);
  const rw = Math.max(4, w * s);
  const rh = Math.max(4, hh * s);
  const el = h("span", { class: "i", "aria-hidden": "true" });
  el.innerHTML = `<svg viewBox="0 0 16 16" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.5"><rect x="${(16 - rw) / 2}" y="${(16 - rh) / 2}" width="${rw}" height="${rh}" rx="1.5"/></svg>`;
  return el;
}

export function assetsPanel(ctx: Ctx, use: "stage" | "scene", o: { embedded?: boolean; onChange?: () => void } = {}): HTMLElement {
  const box = h("div", { class: o.embedded ? "assets-panel embedded" : "card assets-panel" });
  let pollTimer: any = null;

  const flag = (a: Asset) => (use === "stage" ? a.useStage : a.useScene);
  const kindKo = (k: string) => KINDS.find((x) => x.id === k)?.ko || k;
  const patch = async (a: Asset, body: Record<string, unknown>) => {
    try {
      ctx.set(await api<Project>(`/api/projects/${ctx.p.id}/assets/${a.id}`, { method: "PATCH", body }));
      draw();
      o.onChange?.();
      return true;
    } catch (err) {
      toast(err instanceof Error ? err.message : String(err), "err");
      return false;
    }
  };

  function tile(a: Asset): HTMLElement {
    const on = h("input", { type: "checkbox", class: "switch", role: "switch", checked: flag(a), "aria-label": `${a.name} 그림에 붙이기`, "data-k": `use-${a.id}` });
    on.addEventListener("change", () => {
      const used = (ctx.p.data.assets || []).filter(flag).length;
      if (on.checked && used >= MAX_USED) {
        on.checked = false;
        return toast(`한 번에 ${MAX_USED}개까지 붙일 수 있어요.`, "info");
      }
      void patch(a, use === "stage" ? { useStage: on.checked } : { useScene: on.checked });
    });
    const target = a.kind === "led" ? screens(ctx).find((e) => e.id === a.target)?.name : null;
    return h(
      "div",
      { class: `asset-tile${flag(a) ? " on" : ""}` },
      h("div", { class: "asset-img checker" }, h("img", { class: "zoomable", src: mediaUrl(a.key), alt: a.name, loading: "lazy" })),
      h(
        "button",
        { class: "asset-edit", type: "button", title: "이름·종류·쓰는 곳 고치기", onclick: () => editDialog(a.id) },
        h("b", { class: "one-line" }, a.name),
        h("span", { class: "asset-sub" }, h("span", { class: "chip" }, kindKo(a.kind)), a.kind === "led" ? h("span", { class: "xs muted one-line" }, target || "화면 안 정함") : null),
      ),
      h("label", { class: "toggle small asset-use" }, h("span", null, "붙이기"), on),
    );
  }

  /** 에셋 고치기: 칸마다 바로 저장한다 */
  function editDialog(id: string) {
    const body = h("div", { class: "stack" });
    const current = () => (ctx.p.data.assets || []).find((x) => x.id === id);
    const drawBody = () => {
      const a = current();
      clear(body);
      if (!a) return close();
      const name = h("input", { value: a.name, maxlength: 60 });
      name.addEventListener("change", () => void patch(a, { name: name.value }));
      const kind = h("select", null, KINDS.map((k) => h("option", { value: k.id, selected: k.id === a.kind }, k.ko)));
      kind.addEventListener("change", () => void patch(a, { kind: kind.value }).then(drawBody));
      const note = h("input", { value: a.note, maxlength: 300, placeholder: a.kind === "logo" ? "예: 메인 LED 가운데, 지붕 앞면" : a.kind === "ref" ? "예: 무대 위 대형 풍선 오브제" : "예: 메인 LED 가운데에 크게" });
      note.addEventListener("change", () => void patch(a, { note: note.value }));
      const scr = screens(ctx);
      let target: HTMLSelectElement | null = null;
      if (a.kind === "led") {
        target = h("select", null, h("option", { value: "" }, scr.length ? "띄울 화면 고르기" : "화면 요소가 아직 없어요"), scr.map((e) => h("option", { value: e.id, selected: e.id === a.target }, e.name)));
        const t = target;
        t.addEventListener("change", () => void patch(a, { target: t.value }));
      }
      body.append(
        h("div", { class: "asset-preview checker" }, h("img", { src: mediaUrl(a.key), alt: a.name })),
        h("label", null, "이름", name),
        h("label", null, "종류", kind, h("span", { class: "help" }, KINDS.find((k) => k.id === a.kind)?.hint || "")),
        ...(target ? [h("label", null, "띄울 화면", target)] : []),
        h("label", null, h("span", null, "어디에·어떻게 ", h("span", { class: "opt" }, "(선택)")), note),
      );
    };
    const del = h("button", { class: "btn ghost danger", type: "button" }, icon("trash", 16), "지우기");
    const close = modal("에셋 고치기", body, {
      size: "sm",
      sub: "고치는 대로 저장돼요",
      foot: (cl) => [h("div", { class: "left" }, del), h("button", { class: "btn", type: "button", onclick: cl }, "닫기")],
    });
    del.addEventListener("click", async () => {
      const a = current();
      if (!a || !(await confirmBox("에셋 지우기", `"${a.name}" 그림을 보관함에서 지울까요?`, "지우기", { danger: true }))) return;
      try {
        ctx.set(await api<Project>(`/api/projects/${ctx.p.id}/assets/${a.id}`, { method: "DELETE" }));
        close();
        draw();
        o.onChange?.();
      } catch (err) {
        toast(err instanceof Error ? err.message : String(err), "err");
      }
    });
    drawBody();
  }

  function uploadDialog() {
    let kind: AssetKind = use === "stage" ? "logo" : "ref";
    const kindGrid = h("div", { class: "option-grid two" });
    const logoNote = h("p", { class: "help" }, "로고는 배경이 투명한 PNG가 가장 좋아요.");
    const drawKinds = () => {
      clear(kindGrid);
      for (const k of KINDS)
        kindGrid.append(
          h(
            "button",
            {
              type: "button",
              class: `option-card${kind === k.id ? " on" : ""}`,
              "aria-pressed": kind === k.id ? "true" : "false",
              onclick: () => {
                kind = k.id;
                drawKinds();
              },
            },
            h("span", { class: "t" }, k.ko),
            h("span", { class: "d" }, k.hint),
          ),
        );
      logoNote.hidden = kind !== "logo";
    };
    drawKinds();
    const name = h("input", { placeholder: "예: 대회 엠블럼", maxlength: 60 });
    const note = h("input", { placeholder: "예: 메인 LED 가운데에 크게", maxlength: 300 });
    const go = h("button", { class: "btn primary", type: "button" }, icon("upload", 16), "그림 고르기");
    const close = modal(
      "소스 올리기",
      h("div", { class: "stack" }, h("div", { class: "field" }, h("span", { class: "field-label" }, "무엇인가요"), kindGrid, logoNote), h("label", null, "이름", name), h("label", null, h("span", null, "어디에·어떻게 ", h("span", { class: "opt" }, "(선택)")), note)),
      { size: "sm", foot: (cl) => [h("button", { class: "btn ghost", type: "button", onclick: cl }, "취소"), go] },
    );
    go.addEventListener("click", async () => {
      const files = await pickFiles(false);
      if (!files.length) return;
      await busy(go, "올리는 중", async () => {
        const f = files[0];
        // 작은 PNG(로고)는 투명 배경을 살리려고 그대로 올린다
        const keepPng = f.type === "image/png" && f.size < 3.5 * 1024 * 1024;
        const blob = keepPng ? f : (await shrinkImage(f)).blob;
        const q = new URLSearchParams({ kind, name: name.value || f.name.replace(/\.[^.]+$/, ""), note: note.value });
        ctx.set(await api<Project>(`/api/projects/${ctx.p.id}/assets?${q}`, { raw: blob, rawType: keepPng ? "image/png" : "image/jpeg" }));
        close();
        draw();
        o.onChange?.();
      });
    });
  }

  function generateDialog() {
    const scr = screens(ctx);
    const target = h("select", { "aria-label": "어느 화면" }, scr.map((e) => h("option", { value: e.id }, e.name)), h("option", { value: "" }, "직접 적기"));
    const screen = h("input", { placeholder: "예: 원통형 LED 둘레", maxlength: 120 });
    const screenField = h("label", null, "화면 설명", screen);
    const syncScreen = () => (screenField.hidden = Boolean(target.value));
    target.addEventListener("change", syncScreen);
    let aspect = "16:9";
    const aspectSeg = segmented(
      ASPECTS.map((a) => ({ id: a.id, ko: [ratioIcon(a.w, a.h), a.ko], hint: a.hint })),
      aspect,
      (v) => (aspect = v || "16:9"),
      "비율",
      // 폰에서는 2×2 로 놓아 창 밖으로 밀려나지 않게 한다
      { showHint: true, cls: "ratio" },
    );
    const desc = h("textarea", { rows: 4, maxlength: 1200, placeholder: "예: 푸른 강물이 흐르는 추상 그래픽, 가운데 은은한 빛줄기, 대회 상징색(청록·금색)" }) as HTMLTextAreaElement;
    const hasLogo = (ctx.p.data.assets || []).some((a) => a.kind === "logo");
    const withLogo = h("input", { type: "checkbox", class: "switch", role: "switch", checked: false, disabled: !hasLogo });
    const per = state.config?.models.fast?.credits["2K"] ?? 1;
    let count = 1;
    const go = h("button", { class: "btn primary", type: "button" }, withCost("만들기", per));
    const countSeg = segmented(
      [
        { id: "1", ko: "1장" },
        { id: "2", ko: "2장 비교" },
      ],
      "1",
      (v) => {
        count = Number(v || 1);
        go.replaceChildren(...withCost("만들기", per * count));
      },
      "장수",
    );
    const close = modal(
      "LED 화면 만들기",
      h(
        "div",
        { class: "stack" },
        scr.length ? h("label", null, "어느 화면", target) : null,
        screenField,
        h("div", { class: "field" }, h("span", { class: "field-label" }, "비율"), aspectSeg),
        h("label", null, "무엇을 보여 줄까요", desc),
        h("div", { class: "field" }, h("label", { class: "toggle" }, withLogo, "로고 넣기"), hasLogo ? null : h("span", { class: "help" }, "먼저 로고를 올려 주세요")),
        h("div", { class: "field" }, h("span", { class: "field-label" }, "장수"), countSeg),
      ),
      { size: "md", sub: "LED에 띄울 그래픽 한 장을 그려요", foot: (cl) => [h("button", { class: "btn ghost", type: "button", onclick: cl }, "취소"), go] },
    );
    if (!scr.length) target.value = "";
    syncScreen();
    go.addEventListener("click", () =>
      busy(go, "보내는 중", async () => {
        if (!desc.value.trim()) throw new Error("무엇을 보여 줄지 적어 주세요.");
        const el = scr.find((e) => e.id === target.value);
        await api<Cut[]>(`/api/projects/${ctx.p.id}/assets/generate`, { body: { target: target.value, screen: screen.value, aspect, desc: desc.value, withLogo: withLogo.checked, count, name: el ? `${el.name} 화면` : screen.value || "LED 화면" } });
        close();
        await refreshMe();
        toast("LED 화면을 그리기 시작했어요", "info");
        draw();
        void poll();
      }),
    );
  }

  let pending = 0;
  async function poll() {
    clearTimeout(pollTimer);
    if (!document.body.contains(box)) return;
    try {
      const cuts = await api<Cut[]>(`/api/projects/${ctx.p.id}/cuts?phase=asset`);
      const waiting = cuts.filter((c) => c.status === "queued" || c.status === "running");
      nudgeQueued(waiting);
      const failed = cuts.filter((c) => c.status === "failed" && Date.now() - new Date(c.createdAt).getTime() < 5 * 60_000);
      if (waiting.length !== pending) {
        pending = waiting.length;
        ctx.set(await api<Project>(`/api/projects/${ctx.p.id}`));
        draw();
        if (failed.length) await refreshMe();
      }
      if (waiting.length) pollTimer = setTimeout(poll, 3000);
    } catch {
      pollTimer = setTimeout(poll, 5000);
    }
  }

  const draw = () => redraw(box, drawTiles);
  function drawTiles() {
    clear(box);
    const list = ctx.p.data.assets || [];
    const used = list.filter(flag).length;
    const Head = o.embedded ? "h3" : "h2";
    box.append(
      h(
        "div",
        { class: "card-head" },
        h("div", { class: "card-head-text" }, h(Head, { class: "head-title" }, icon("layers", 18), "소스·에셋", h("span", { class: "chip num", title: `한 번에 ${MAX_USED}개까지 붙여요` }, `붙임 ${used}/${MAX_USED}`)), h("p", { class: "sub" }, "켠 것만 그림에 붙어요")),
        h("div", { class: "right" }, h("button", { class: "btn small", type: "button", onclick: uploadDialog }, icon("upload", 16), "올리기"), h("button", { class: "btn small", type: "button", onclick: generateDialog }, icon("monitor", 16), h("span", { class: "hide-sm" }, "LED 화면 만들기"), h("span", { class: "show-sm" }, "LED 화면"))),
      ),
      list.length || pending
        ? h("div", { class: "asset-grid", "data-gallery": `assets-${use}` }, list.map(tile), Array.from({ length: pending }, () => h("div", { class: "asset-tile skeleton-tile" }, h("div", { class: "asset-img skeleton", "aria-hidden": "true" }), h("div", { class: "skeleton", "aria-hidden": "true", style: { height: "12px", width: "70%" } }), h("span", { class: "xs muted" }, h("span", { class: "sr-only" }, "LED 화면 "), "그리는 중"))))
        : h("p", { class: "assets-empty small muted" }, icon("layers", 16), "로고나 참고 그림을 올려 두세요"),
    );
  }

  draw();
  // 화면에 붙은 뒤에 처음 확인한다 (붙기 전에 부르면 바로 끝나 그리는 중인 화면을 놓친다)
  setTimeout(() => void poll(), 0);
  return box;
}
